import React from 'react';

/**
 * Lightweight markdown renderer supporting:
 * Bold, Italic, Strikethrough, Links, Lists, Blockquotes, Tables
 */

// Parse inline markdown (bold, italic, strikethrough, links)
// `linkColor` is an optional CSS color used for `[text](url)` links. When not
// provided, links fall back to the default blue palette.
function parseInline(text, linkColor) {
  if (!text) return text;
  const parts = [];
  let remaining = text;
  let key = 0;

  const linkClass = linkColor
    ? 'underline hover:opacity-80 transition-opacity'
    : 'text-blue-600 underline hover:text-blue-800';
  const linkStyle = linkColor ? { color: linkColor } : undefined;

  const patterns = [
    // Links: [text](url)
    { regex: /\[([^\]]+)\]\(([^)]+)\)/, render: (m) => <a key={key++} href={m[2]} target="_blank" rel="noopener noreferrer" className={linkClass} style={linkStyle}>{m[1]}</a> },
    // Bold: **text** or __text__
    { regex: /\*\*(.+?)\*\*|__(.+?)__/, render: (m) => <strong key={key++}>{m[1] || m[2]}</strong> },
    // Strikethrough: ~~text~~
    { regex: /~~(.+?)~~/, render: (m) => <s key={key++}>{m[1]}</s> },
    // Italic: *text* or _text_
    { regex: /\*(.+?)\*|_(.+?)_/, render: (m) => <em key={key++}>{m[1] || m[2]}</em> },
  ];

  while (remaining.length > 0) {
    let earliest = null;
    let earliestIndex = Infinity;
    let earliestPattern = null;

    for (const p of patterns) {
      const match = remaining.match(p.regex);
      if (match && match.index < earliestIndex) {
        earliest = match;
        earliestIndex = match.index;
        earliestPattern = p;
      }
    }

    if (earliest && earliestPattern) {
      if (earliestIndex > 0) {
        parts.push(remaining.substring(0, earliestIndex));
      }
      parts.push(earliestPattern.render(earliest));
      remaining = remaining.substring(earliestIndex + earliest[0].length);
    } else {
      parts.push(remaining);
      break;
    }
  }

  return parts.length === 1 && typeof parts[0] === 'string' ? parts[0] : parts;
}

// Parse a full markdown string into React elements
// Accepts optional options: { linkColor: '#FF6B6B' } to recolor links.
export function renderMarkdown(text, options = {}) {
  if (!text) return null;
  // Defensive: if the caller hands us a React element (or any non-string),
  // pass it straight through instead of crashing on `.split`. This lets
  // mixed-type article maps (some strings, some JSX) share the same
  // renderer without bugs.
  if (typeof text !== 'string') return text;
  const { linkColor } = options;
  const inline = (s) => parseInline(s, linkColor);

  const lines = text.split('\n');
  const elements = [];
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Fenced code block ```lang ... ```
    if (line.trimStart().startsWith('```')) {
      const codeLines = [];
      let j = i + 1;
      while (j < lines.length && !lines[j].trimStart().startsWith('```')) {
        codeLines.push(lines[j]);
        j++;
      }
      elements.push(
        <pre key={key++} className="bg-zinc-900 text-zinc-100 rounded-lg p-4 my-4 overflow-x-auto text-sm">
          <code className="font-mono whitespace-pre">{codeLines.join('\n')}</code>
        </pre>
      );
      i = j + 1; // skip closing fence
      continue;
    }

    // Headings: # H1, ## H2, ### H3, #### H4
    const headingMatch = line.match(/^(#{1,4})\s+(.+)$/);
    if (headingMatch) {
      const level = headingMatch[1].length;
      const txt = headingMatch[2].trim();
      const sizes = ['text-3xl md:text-4xl mt-10 mb-4', 'text-2xl md:text-3xl mt-8 mb-3', 'text-xl mt-6 mb-2', 'text-lg mt-4 mb-2'];
      const Tag = `h${level}`;
      elements.push(
        React.createElement(Tag, {
          key: key++,
          className: `font-bold text-zinc-900 ${sizes[level - 1]}`,
        }, inline(txt))
      );
      i++;
      continue;
    }

    // Standalone image: ![alt](url)
    const imgMatch = line.trim().match(/^!\[([^\]]*)\]\(([^)]+)\)$/);
    if (imgMatch) {
      elements.push(
        <img
          key={key++}
          src={imgMatch[2]}
          alt={imgMatch[1]}
          className="w-full rounded-lg my-4 border border-zinc-200"
          loading="lazy"
        />
      );
      i++;
      continue;
    }

    // Horizontal rule
    if (/^\s*(-{3,}|\*{3,})\s*$/.test(line)) {
      elements.push(<hr key={key++} className="my-6 border-zinc-200" />);
      i++;
      continue;
    }

    // Table: starts with |
    if (line.trim().startsWith('|') && i + 1 < lines.length && lines[i + 1].trim().match(/^\|[\s\-|]+\|$/)) {
      const tableLines = [];
      let j = i;
      while (j < lines.length && lines[j].trim().startsWith('|')) {
        tableLines.push(lines[j]);
        j++;
      }
      // Parse header
      const parseRow = (row) => row.split('|').filter((_, idx, arr) => idx > 0 && idx < arr.length - 1).map(c => c.trim());
      const headers = parseRow(tableLines[0]);
      const rows = tableLines.slice(2).map(parseRow);

      elements.push(
        <div key={key++} className="overflow-x-auto my-2">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b border-zinc-300">
                {headers.map((h, hi) => <th key={hi} className="text-left px-3 py-1.5 font-semibold text-zinc-700">{inline(h)}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, ri) => (
                <tr key={ri} className="border-b border-zinc-100">
                  {row.map((cell, ci) => <td key={ci} className="px-3 py-1.5 text-zinc-600">{inline(cell)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      i = j;
      continue;
    }

    // Blockquote: > text
    if (line.trimStart().startsWith('> ')) {
      const quoteLines = [];
      let j = i;
      while (j < lines.length && lines[j].trimStart().startsWith('> ')) {
        quoteLines.push(lines[j].trimStart().substring(2));
        j++;
      }
      elements.push(
        <blockquote key={key++} className="border-l-3 border-zinc-300 pl-3 my-1.5 text-zinc-500 italic">
          {quoteLines.map((ql, qi) => <p key={qi}>{inline(ql)}</p>)}
        </blockquote>
      );
      i = j;
      continue;
    }

    // Unordered list: - item or + item
    if (line.trimStart().match(/^[-+]\s/)) {
      const listItems = [];
      let j = i;
      while (j < lines.length && lines[j].trimStart().match(/^[-+]\s/)) {
        listItems.push(lines[j].trimStart().replace(/^[-+]\s/, ''));
        j++;
      }
      elements.push(
        <ul key={key++} className="list-disc pl-5 my-1.5 space-y-0.5 text-zinc-600">
          {listItems.map((li, idx) => <li key={idx}>{inline(li)}</li>)}
        </ul>
      );
      i = j;
      continue;
    }

    // Ordered list: 1. item
    if (line.trimStart().match(/^\d+\.\s/)) {
      const listItems = [];
      let j = i;
      while (j < lines.length && lines[j].trimStart().match(/^\d+\.\s/)) {
        listItems.push(lines[j].trimStart().replace(/^\d+\.\s/, ''));
        j++;
      }
      elements.push(
        <ol key={key++} className="list-decimal pl-5 my-1.5 space-y-0.5 text-zinc-600">
          {listItems.map((li, idx) => <li key={idx}>{inline(li)}</li>)}
        </ol>
      );
      i = j;
      continue;
    }

    // Empty line — emit an explicit blank-line spacer so paragraph breaks
    // produce visible vertical space even when the parent line-height is tight.
    if (line.trim() === '') {
      // Coalesce runs of blank lines into a single break
      if (elements.length > 0) {
        elements.push(<div key={key++} className="h-3" aria-hidden />);
      }
      i++;
      continue;
    }

    // Regular paragraph
    elements.push(<p key={key++} className="text-zinc-600">{inline(line)}</p>);
    i++;
  }

  return <div className="space-y-1">{elements}</div>;
}
